import Axios, {
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import queryString from 'query-string';
import { retrieveTokens } from 'amazon-cognito-passwordless-auth/storage';

interface ApiConfig {
  SERVER_URL: string;
  ORG_NAME: string | undefined;
  DEFAULT_HEADERS: Record<string, string>;
  REQUEST_TIMEOUT: number;
}

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  isUnauthorized?: boolean;
}

interface BaseApiConfig extends Omit<AxiosRequestConfig, 'params'> {
  path: string;
  param?: Record<string, any>;
  isUnauthorized?: boolean;
}

interface MutationApiConfig extends BaseApiConfig {
  body?: any;
}

interface FormDataApiConfig extends MutationApiConfig {
  onProgress?: (percentCompleted: number) => void;
}

const API_CONFIG: ApiConfig = {
  SERVER_URL: `${process.env.NEXT_PUBLIC_SERVER_V2_URL || 'http://localhost:3000'}/${process.env.NEXT_PUBLIC_ORGANIZATION_NAME || 'AOL'}/`,
  ORG_NAME: process.env.NEXT_PUBLIC_ORGANIZATION_NAME,
  DEFAULT_HEADERS: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...(process.env.NEXT_PUBLIC_API_CLIENT_ID && {
      'x-client-id': process.env.NEXT_PUBLIC_API_CLIENT_ID,
    }),
  },
  REQUEST_TIMEOUT: 60000, // 60 seconds (handles Heroku cold starts and slow networks)
};

const axiosClient = Axios.create({
  baseURL: API_CONFIG.SERVER_URL,
  headers: API_CONFIG.DEFAULT_HEADERS,
  timeout: API_CONFIG.REQUEST_TIMEOUT,
});

axiosClient.interceptors.request.use(async function (
  config: CustomAxiosRequestConfig
): Promise<InternalAxiosRequestConfig> {
  try {
    if (!config.isUnauthorized) {
      const { accessToken } = await retrieveTokens();
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
    }
  } catch {
    // No logged-in user: don't set auth header
  }
  return config;
});

// Remove global error interceptor - handle errors in individual methods instead

/**
 * Extract error message from error object (for React Query errors)
 * This is a public utility that can be used in components
 */
export function getApiErrorMessage(error: any): string {
  // Check if it's an Axios error with response
  if (error?.response?.data) {
    const { data } = error.response;

    // Try to extract message from data.data array first (validation errors)
    if (data?.data && Array.isArray(data.data) && data.data[0]?.message) {
      if (data.data.length > 1) {
        const validationErrors = data.data
          .map((err: any) => err.message)
          .join(', ');
        return `Validation Error: ${validationErrors}`;
      }
      return data.data[0].message;
    }

    // Try to extract from data.message
    if (data?.message) {
      return data.message;
    }
  }

  // Check if it's a network error
  if (error?.message === 'Network Error' || !error?.response) {
    return 'Network error. Please check your internet connection.';
  }

  // Fallback to error message
  return error?.message || 'An unexpected error occurred. Please try again.';
}

function generateQueryString(params?: Record<string, any>): string {
  if (
    !params ||
    typeof params !== 'object' ||
    Object.keys(params).length === 0
  ) {
    return '';
  }

  // Filter out undefined and null values for cleaner URLs
  const cleanParams = Object.entries(params).reduce<Record<string, any>>(
    (acc, [key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        acc[key] = value;
      }
      return acc;
    },
    {}
  );

  return queryString.stringify(cleanParams, {
    skipNull: true,
    skipEmptyString: true,
    arrayFormat: 'comma',
  });
}

export const api = {
  async get<T = any>(config: BaseApiConfig): Promise<T> {
    const { param, path, signal, ...rest } = config;
    const queryParams = generateQueryString(param);
    const url = queryParams ? `${path}?${queryParams}` : path;

    try {
      const result: AxiosResponse<T> = await axiosClient.get(url, {
        signal,
        ...rest,
      });
      return result.data;
    } catch (error) {
      throw error;
    }
  },

  async getFile(config: BaseApiConfig): Promise<Blob> {
    const { param, path, signal, ...rest } = config;
    const queryParams = generateQueryString(param);
    const url = queryParams ? `${path}?${queryParams}` : path;

    try {
      const result: AxiosResponse<Blob> = await axiosClient.get(url, {
        responseType: 'blob',
        signal,
        ...rest,
      });
      return result.data;
    } catch (error) {
      throw error;
    }
  },

  async post<T = any>(config: MutationApiConfig): Promise<T> {
    const { body, param, path, signal, ...rest } = config;
    const queryParams = generateQueryString(param);
    const url = queryParams ? `${path}?${queryParams}` : path;

    try {
      const result: AxiosResponse<T> = await axiosClient.post(url, body, {
        signal,
        ...rest,
      });
      return result.data;
    } catch (error) {
      throw error;
    }
  },

  async postFormData<T = any>(config: FormDataApiConfig): Promise<T> {
    const { body, param, path, onProgress, signal, ...rest } = config;
    const queryParams = generateQueryString(param);
    const url = queryParams ? `${path}?${queryParams}` : path;

    try {
      const result: AxiosResponse<T> = await axiosClient.post(url, body, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: onProgress
          ? (progressEvent) => {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) /
                  (progressEvent.total || progressEvent.loaded)
              );
              onProgress(percentCompleted);
            }
          : undefined,
        signal,
        ...rest,
      });
      return result.data;
    } catch (error) {
      throw error;
    }
  },

  async putFormData<T = any>(config: FormDataApiConfig): Promise<T> {
    const { body, param, path, onProgress, signal, ...rest } = config;
    const queryParams = generateQueryString(param);
    const url = queryParams ? `${path}?${queryParams}` : path;

    try {
      const result: AxiosResponse<T> = await axiosClient.put(url, body, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: onProgress
          ? (progressEvent) => {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) /
                  (progressEvent.total || progressEvent.loaded)
              );
              onProgress(percentCompleted);
            }
          : undefined,
        signal,
        ...rest,
      });
      return result.data;
    } catch (error) {
      throw error;
    }
  },

  async put<T = any>(config: MutationApiConfig): Promise<T> {
    const { body, param, path, signal, ...rest } = config;
    const queryParams = generateQueryString(param);
    const url = queryParams ? `${path}?${queryParams}` : path;

    try {
      const result: AxiosResponse<T> = await axiosClient.put(url, body, {
        headers: { 'Content-Type': 'application/json' },
        signal,
        ...rest,
      });
      return result.data;
    } catch (error) {
      throw error;
    }
  },

  async delete<T = any>(config: MutationApiConfig): Promise<T> {
    const { body, param, path, signal, ...rest } = config;
    const queryParams = generateQueryString(param);
    const url = queryParams ? `${path}?${queryParams}` : path;

    try {
      const result: AxiosResponse<T> = await axiosClient.delete(url, {
        data: body,
        signal,
        ...rest,
      });
      return result.data;
    } catch (error) {
      throw error;
    }
  },
};
